import { useEffect, useRef, useState } from "react";
import { Plus, MoreHorizontal, Trash2 } from "lucide-react";
import clsx from "clsx";
import { useConfirm } from "../lib/confirm";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import type { Card as CardT, Lane as LaneT } from "../lib/types";
import { ACCENTS, accentClasses } from "../lib/types";
import { CardItem } from "./CardItem";

type Props = {
  lane: LaneT;
  cards: CardT[];
  onAddCard: (title: string) => void;
  onCardClick: (card: CardT) => void;
  onRenameLane: (name: string) => void;
  onChangeAccent: (accent: LaneT["accent"]) => void;
  onDeleteLane: () => void;
};

export function LaneColumn({
  lane,
  cards,
  onAddCard,
  onCardClick,
  onRenameLane,
  onChangeAccent,
  onDeleteLane,
}: Props) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const confirm = useConfirm();

  // Close the kebab menu on any click outside of it (or Escape). The old
  // onMouseLeave fallback didn't fire for clicks on neutral areas, so the
  // menu would linger on screen.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (menuTriggerRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const accent = accentClasses[lane.accent];

  const { attributes, listeners, setNodeRef: setSortableRef, transform, transition, isDragging } =
    useSortable({ id: lane.id, data: { type: "lane", lane } });

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `lane-drop-${lane.id}`,
    data: { type: "lane", laneId: lane.id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setSortableRef}
      style={style}
      className={clsx(
        // w-full + min-w-0 lets the lane fluidly fill whichever
        // masonry column it lands in. The parent column enforces the
        // min-width so lanes never squeeze below the readable floor.
        "flex h-fit w-full min-w-0 flex-col rounded-lane border",
        accent.laneBorder,
        accent.laneBg
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className="flex cursor-grab items-center gap-2 px-3.5 py-3 active:cursor-grabbing"
      >
        <span className={clsx("h-1.5 w-1.5 rounded-full", accent.dot)} />
        {renaming ? (
          <input
            autoFocus
            defaultValue={lane.name}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== lane.name) onRenameLane(v);
              setRenaming(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              if (e.key === "Escape") setRenaming(false);
            }}
            className="input-bare flex-1 text-sm font-medium"
          />
        ) : (
          <button
            onDoubleClick={() => setRenaming(true)}
            className="flex-1 cursor-text text-left text-sm font-medium tracking-tight"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {lane.name}
          </button>
        )}
        <span className="font-mono text-[10px] text-ink-mute">{cards.length}</span>
        <div className="relative">
          <button
            ref={menuTriggerRef}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="rounded p-0.5 text-ink-mute hover:bg-surface/70 hover:text-ink"
          >
            <MoreHorizontal size={14} />
          </button>
          {menuOpen && (
            <div
              ref={menuRef}
              className="absolute right-0 top-7 z-30 w-40 rounded-lg border border-border bg-surface p-1 shadow-modal"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-ink-soft hover:bg-canvas hover:text-ink"
                onClick={() => {
                  setRenaming(true);
                  setMenuOpen(false);
                }}
              >
                rename
              </button>
              <div className="my-1 h-px bg-border/60" />
              <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-ink-mute">
                color
              </div>
              <div className="grid grid-cols-7 gap-1 px-2 py-1">
                {ACCENTS.map((a) => (
                  <button
                    key={a}
                    title={a}
                    onClick={() => {
                      onChangeAccent(a);
                      setMenuOpen(false);
                    }}
                    className={clsx(
                      "h-4 w-4 rounded-full border border-black/5 transition-transform hover:scale-110",
                      accentClasses[a].dot,
                      lane.accent === a && "ring-2 ring-offset-1 ring-ink-faint"
                    )}
                  />
                ))}
              </div>
              <div className="my-1 h-px bg-border/60" />
              <button
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-blush-400 hover:bg-blush-50"
                onClick={async () => {
                  setMenuOpen(false);
                  if (cards.length === 0) {
                    onDeleteLane();
                    return;
                  }
                  const ok = await confirm({
                    title: `delete lane "${lane.name}"?`,
                    body: `${cards.length} ${
                      cards.length === 1 ? "card" : "cards"
                    } inside will become loose (they'll show up in the inbox).`,
                    confirmLabel: "delete lane",
                    danger: true,
                  });
                  if (ok) onDeleteLane();
                }}
              >
                <Trash2 size={12} /> delete lane
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        ref={setDroppableRef}
        className={clsx(
          "flex flex-col gap-2 px-2 pb-2 transition-colors",
          isOver && "bg-surface/30"
        )}
      >
        <SortableContext items={cards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {cards.map((c) => (
            <CardItem
              key={c.id}
              card={c}
              fallbackAccent={lane.accent}
              onClick={() => onCardClick(c)}
            />
          ))}
        </SortableContext>

        {adding ? (
          <div className="rounded-card border border-border bg-surface p-2 shadow-card">
            <input
              autoFocus
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newTitle.trim()) {
                  onAddCard(newTitle.trim());
                  setNewTitle("");
                  // keep open for rapid capture
                } else if (e.key === "Escape") {
                  setAdding(false);
                  setNewTitle("");
                }
              }}
              onBlur={() => {
                if (newTitle.trim()) {
                  onAddCard(newTitle.trim());
                  setNewTitle("");
                }
                setAdding(false);
              }}
              placeholder="what's on your mind?"
              className="input-bare w-full px-1.5 py-1 text-sm"
            />
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center justify-center gap-1.5 rounded-card px-3 py-2 text-xs text-ink-mute transition-colors hover:bg-surface/60 hover:text-ink-soft"
          >
            <Plus size={12} /> add card
          </button>
        )}
      </div>
    </div>
  );
}
